import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Globe2, Users, Handshake, CalendarDays, Award, Loader2, Send, MessageCircle,
} from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { IMAGES, LINKS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = [
  "Hotellerie e ricettività",
  "Tour operator e agenzie di viaggio",
  "Trasporti e compagnie aeree",
  "Ristorazione e food",
  "Enti e istituzioni del turismo",
  "Eventi e MICE",
  "Altro settore del turismo",
];

const YEARS = ["Meno di 3 anni", "3 – 5 anni", "6 – 10 anni", "Oltre 10 anni"];

const PEOPLE = [
  {
    icon: Award,
    name: "Laure Hila",
    role: "Ideatrice e promotrice del progetto",
    text: "Il progetto nasce dalla mia iniziativa e dal desiderio di portare in Albania una rete internazionale di professionisti del turismo.",
  },
  {
    icon: Users,
    name: "Ingrid Struga",
    role: "Collaboratrice del progetto",
    text: "Collabora allo sviluppo del progetto sul territorio albanese, tra relazioni locali e conoscenza del settore.",
  },
  {
    icon: Handshake,
    name: "Skål Italia — Tito Livio",
    role: "Supporto istituzionale",
    text: "Il progetto può contare sul supporto di Skål Italia, il club italiano della rete internazionale.",
  },
];

const PROFILES = [
  "Dirigenti e manager del settore turistico e dell'ospitalità",
  "Imprenditori di hotel, resort, agenzie di viaggio e tour operator",
  "Professionisti di trasporti, eventi, ristorazione e accoglienza",
  "Rappresentanti di enti e istituzioni legate al turismo",
];

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  city_country: "",
  role: "",
  organization: "",
  sector: SECTORS[0],
  experience_years: YEARS[0],
  experience: "",
  motivation: "",
  contact_preference: "Email",
  privacy: false,
};

const inputCls =
  "w-full px-4 py-3.5 rounded-lg bg-cream border border-line focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 text-ink placeholder:text-stone/50 transition-[border-color,box-shadow] duration-300";
const labelCls = "block text-xs uppercase tracking-[0.18em] font-semibold text-stone mb-2";

export default function Skal() {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Per favore accetta la Privacy Policy per continuare.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/skal/candidature`, form);
      toast.success("Candidatura inviata. Grazie, ti ricontatterò personalmente.");
      setForm(emptyForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Qualcosa non ha funzionato. Riprova tra poco o scrivimi dalla pagina contatti."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="skal-page">
      <Seo
        title="Skål International Albania — Il progetto | Laure Hila"
        description="Il progetto Skål International Albania: un club della rete mondiale dei professionisti del turismo, tra Italia e Albania. Ideato e promosso da Laure Hila. Candidature aperte."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Skål International Albania — Il progetto",
          description:
            "Progetto per la creazione di un club Skål International in Albania, ideato e promosso da Laure Hila.",
          inLanguage: "it-IT",
        }}
      />

      {/* HERO */}
      <section data-testid="skal-hero" className="pt-32 md:pt-44 pb-16 md:pb-24 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7">
            <FadeIn>
              <p className="eyebrow mb-5">Progetto istituzionale</p>
            </FadeIn>
            <h1 className="font-serif font-medium text-ink leading-[1.05] tracking-tight">
              <MaskedLine delay={0.1} className="text-5xl md:text-7xl">
                Skål International
              </MaskedLine>
              <MaskedLine delay={0.25} className="text-5xl md:text-7xl italic text-gold">
                Albania
              </MaskedLine>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-stone">
                Un progetto per portare in Albania la più grande rete internazionale
                di professionisti e dirigenti del settore turistico: relazioni,
                amicizie professionali e opportunità di collaborazione, tra l'Italia
                e la terra delle mie radici.
              </p>
            </FadeIn>
          </div>
          <Reveal delay={0.15} className="md:col-span-5">
            <div className="frame-gold max-w-[420px] mx-auto md:ml-auto">
              <div className="relative z-10 overflow-hidden rounded-xl">
                <img
                  src={IMAGES.kruja.url}
                  alt="Kruja, Albania: il museo nazionale di Skanderbeg, simbolo del territorio del progetto Skål Albania"
                  style={{ objectPosition: "center 45%" }}
                  className="w-full aspect-[4/5] object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COS'È SKÅL */}
      <section data-testid="skal-about" className="py-20 md:py-28 bg-sand/60 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className="eyebrow mb-4">La rete internazionale</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-ink text-balance">
                Cos'è <span className="italic text-gold">Skål International</span>.
              </h2>
              <p className="mt-7 text-base md:text-lg leading-relaxed text-stone">
                Skål International è un'organizzazione internazionale, fondata nel
                1934, che riunisce professionisti e dirigenti del settore turistico
                e dell'ospitalità in tutto il mondo. Non è un'associazione
                commerciale: è una rete costruita sulle relazioni, sull'amicizia
                professionale e sulla collaborazione tra chi lavora nel turismo.
              </p>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-stone">
                I club Skål nascono territorio per territorio e si incontrano
                regolarmente, creando opportunità concrete di scambio, sviluppo e
                amicizia tra professionisti di paesi diversi.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.12} className="md:col-span-5">
            <div className="overflow-hidden rounded-xl">
              <img
                src={IMAGES.hipAwards.url}
                alt="Laure Hila a un evento professionale del settore turistico e dell'ospitalità in Italia"
                style={{ objectPosition: "center 30%" }}
                className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* IL PROGETTO */}
      <section data-testid="skal-progetto" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-3xl">
            <p className="eyebrow mb-4">Il progetto</p>
            <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-ink text-balance">
              Un club Skål per <span className="italic text-gold">l'Albania</span>.
            </h2>
            <p className="mt-7 text-base md:text-lg leading-relaxed text-stone">
              Il progetto Skål International Albania nasce dalla mia iniziativa e
              dal legame profondo con la mia terra di origine: creare un punto di
              incontro stabile per i professionisti del turismo albanese e
              rafforzare le relazioni tra Italia e Albania, due paesi che il mare
              unisce più di quanto divida.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12 md:mt-16">
            {PEOPLE.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`skal-person-${i}`}
                  className="h-full p-7 md:p-8 rounded-2xl bg-cream border border-line hover:border-gold transition-colors duration-500"
                >
                  <p.icon size={26} className="text-gold" strokeWidth={1.5} />
                  <h3 className="mt-5 font-serif text-2xl text-ink">{p.name}</h3>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-gold font-semibold">
                    {p.role}
                  </p>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-stone">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10 md:mt-12 p-7 md:p-8 rounded-2xl bg-sand/70 border border-line flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <span className="w-12 h-12 rounded-full bg-ink text-gold flex items-center justify-center flex-shrink-0">
                <CalendarDays size={22} strokeWidth={1.6} />
              </span>
              <div>
                <p className="font-serif text-xl md:text-2xl text-ink">
                  Evento di lancio previsto per gennaio 2027
                </p>
                <p className="mt-1 text-sm md:text-base text-stone">
                  Sede, programma e dettagli organizzativi sono in definizione e
                  saranno comunicati su questa pagina.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* A CHI È RIVOLTA */}
      <section data-testid="skal-profili" className="py-20 md:py-28 bg-ink text-cream grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className="eyebrow mb-4">A chi è rivolta</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-balance">
                Professionisti del turismo, <span className="italic text-gold">un'unica rete</span>.
              </h2>
              <p className="mt-7 text-base md:text-lg leading-relaxed text-cream/70">
                La candidatura è aperta a chi opera professionalmente nel turismo e
                nell'ospitalità, in Albania o con legami concreti con il territorio
                albanese, e desidera far parte di una rete internazionale di
                relazioni professionali.
              </p>
            </Reveal>
            <div className="mt-8 flex flex-col gap-4">
              {PROFILES.map((t, i) => (
                <Reveal key={t} delay={0.08 * i}>
                  <div className="flex items-start gap-4">
                    <Globe2 size={18} className="text-gold mt-1 flex-shrink-0" strokeWidth={1.6} />
                    <p className="text-base md:text-lg text-cream/85">{t}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.12} className="md:col-span-5">
            <div className="overflow-hidden rounded-xl">
              <img
                src={IMAGES.shala.url}
                alt="Il fiume Shala tra le montagne albanesi, il territorio del progetto Skål International Albania"
                style={{ objectPosition: "center 60%" }}
                className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MODULO CANDIDATURA */}
      <section data-testid="skal-candidatura" className="py-20 md:py-32 grain relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-12 md:mb-16">
            <p className="eyebrow mb-4">Candidature aperte</p>
            <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-ink">
              Invia la tua <span className="italic text-gold">candidatura</span>.
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-stone">
              Compila il modulo in cinque passaggi: ogni candidatura viene letta
              personalmente e valutata con attenzione. Ti ricontatterò io stessa.
            </p>
          </Reveal>

          <form
            onSubmit={onSubmit}
            data-testid="skal-form"
            className="p-7 md:p-10 rounded-2xl bg-sand/70 border border-line"
          >
            {/* 01 — Dati personali */}
            <fieldset className="mb-10">
              <legend className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-cream border border-gold flex items-center justify-center font-serif text-sm text-gold font-semibold">01</span>
                <span className="font-serif text-2xl text-ink">Dati personali</span>
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="skal-first-name" className={labelCls}>Nome</label>
                  <input id="skal-first-name" data-testid="skal-first-name" required minLength={2}
                    value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    placeholder="Il tuo nome" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="skal-last-name" className={labelCls}>Cognome</label>
                  <input id="skal-last-name" data-testid="skal-last-name" required minLength={2}
                    value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    placeholder="Il tuo cognome" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="skal-email" className={labelCls}>Email</label>
                  <input id="skal-email" data-testid="skal-email" type="email" required
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="la.tua@email.it" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="skal-phone" className={labelCls}>Telefono</label>
                  <input id="skal-phone" data-testid="skal-phone" type="tel" required minLength={5}
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+39 … / +355 …" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="skal-city" className={labelCls}>Città e paese</label>
                  <input id="skal-city" data-testid="skal-city" required minLength={2}
                    value={form.city_country} onChange={(e) => setForm({ ...form, city_country: e.target.value })}
                    placeholder="Es. Tirana, Albania" className={inputCls} />
                </div>
              </div>
            </fieldset>

            {/* 02 — Profilo professionale */}
            <fieldset className="mb-10">
              <legend className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-cream border border-gold flex items-center justify-center font-serif text-sm text-gold font-semibold">02</span>
                <span className="font-serif text-2xl text-ink">Profilo professionale</span>
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="skal-role" className={labelCls}>Ruolo attuale</label>
                  <input id="skal-role" data-testid="skal-role" required minLength={2}
                    value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                    placeholder="Es. Direttore di hotel" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="skal-organization" className={labelCls}>Azienda / organizzazione (facoltativo)</label>
                  <input id="skal-organization" data-testid="skal-organization"
                    value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
                    placeholder="Nome della tua realtà" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="skal-sector" className={labelCls}>Settore nel turismo</label>
                  <select id="skal-sector" data-testid="skal-sector"
                    value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}
                    className={inputCls}>
                    {SECTORS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            </fieldset>

            {/* 03 — Esperienza */}
            <fieldset className="mb-10">
              <legend className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-cream border border-gold flex items-center justify-center font-serif text-sm text-gold font-semibold">03</span>
                <span className="font-serif text-2xl text-ink">Esperienza</span>
              </legend>
              <div className="grid grid-cols-1 gap-5">
                <div>
                  <label htmlFor="skal-years" className={labelCls}>Anni di esperienza nel settore</label>
                  <select id="skal-years" data-testid="skal-years"
                    value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
                    className={inputCls}>
                    {YEARS.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="skal-experience" className={labelCls}>Racconta la tua esperienza professionale</label>
                  <textarea id="skal-experience" data-testid="skal-experience" required minLength={10} rows={5}
                    value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })}
                    placeholder="Il tuo percorso nel turismo e nell'ospitalità, le responsabilità ricoperte…"
                    className={`${inputCls} resize-y`} />
                </div>
              </div>
            </fieldset>

            {/* 04 — Motivazione */}
            <fieldset className="mb-10">
              <legend className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-cream border border-gold flex items-center justify-center font-serif text-sm text-gold font-semibold">04</span>
                <span className="font-serif text-2xl text-ink">Motivazione</span>
              </legend>
              <div>
                <label htmlFor="skal-motivation" className={labelCls}>Perché vuoi far parte di Skål International Albania?</label>
                <textarea id="skal-motivation" data-testid="skal-motivation" required minLength={10} rows={5}
                  value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                  placeholder="Cosa ti spinge a candidarti e cosa vorresti portare nella rete…"
                  className={`${inputCls} resize-y`} />
              </div>
            </fieldset>

            {/* 05 — Contatto e consenso */}
            <fieldset>
              <legend className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-cream border border-gold flex items-center justify-center font-serif text-sm text-gold font-semibold">05</span>
                <span className="font-serif text-2xl text-ink">Contatto e consenso</span>
              </legend>
              <div className="grid grid-cols-1 gap-5">
                <div>
                  <label htmlFor="skal-contact-pref" className={labelCls}>Come preferisci essere ricontattata/o?</label>
                  <select id="skal-contact-pref" data-testid="skal-contact-pref"
                    value={form.contact_preference} onChange={(e) => setForm({ ...form, contact_preference: e.target.value })}
                    className={inputCls}>
                    <option value="Email">Email</option>
                    <option value="Telefono">Telefono</option>
                    <option value="WhatsApp">WhatsApp</option>
                  </select>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" data-testid="skal-privacy-checkbox"
                    checked={form.privacy}
                    onChange={(e) => setForm({ ...form, privacy: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-[#C5A059]" />
                  <span className="text-xs leading-relaxed text-stone">
                    Ho letto la{" "}
                    <a href="/privacy-policy" className="underline underline-offset-2 hover:text-gold">
                      Privacy Policy
                    </a>{" "}
                    e acconsento al trattamento dei miei dati per la valutazione della candidatura.
                  </span>
                </label>
              </div>
            </fieldset>

            <button
              type="submit"
              data-testid="skal-submit-button"
              disabled={sending}
              className="mt-9 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full bg-ink text-cream font-semibold text-sm tracking-wide hover:bg-gold disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
            >
              {sending ? (
                <>
                  <Loader2 size={17} className="animate-spin" />
                  Invio in corso…
                </>
              ) : (
                <>
                  <Send size={16} />
                  Invia la candidatura
                </>
              )}
            </button>
          </form>

          <Reveal delay={0.1}>
            <p className="mt-10 text-center text-sm md:text-base text-stone">
              Preferisci parlarne di persona?{" "}
              <Link to="/contatti" data-testid="skal-cta-contatti" className="text-gold font-semibold link-underline">
                Scrivimi dalla pagina contatti
              </Link>{" "}
              o{" "}
              <a href={LINKS.whatsapp} target="_blank" rel="noopener noreferrer"
                data-testid="skal-cta-whatsapp"
                className="text-gold font-semibold link-underline inline-flex items-center gap-1">
                <MessageCircle size={14} /> su WhatsApp
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
